import { z } from "zod";

/** Default number of items per page. */
export const DEFAULT_PAGE_SIZE = 20;

/** Maximum allowed page size. */
export const MAX_PAGE_SIZE = 500;

/** Reusable pagination input schema. Merge with your action schema via `.merge()` or `.extend()`. */
export const paginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export type PaginationInput = z.infer<typeof paginationSchema>;

/** Sort direction for server-side sorting. */
export const sortDirEnum = z.enum(["asc", "desc"]);

export type SortDir = z.infer<typeof sortDirEnum>;

/**
 * Reusable server-side sort input schema. Keys are a WHITELIST — the router
 * maps each key to a concrete column/SQL expression, so no raw client string
 * ever reaches `orderBy`.
 *
 * `sortBy` is optional: when omitted, the router applies the page's default
 * ordering. `defaultDir` should match the page's default direction.
 *
 * Usage: `paginationSchema.extend({...}).merge(sortSchema(ITEMS_SORT_KEYS))`
 * with `export const ITEMS_SORT_KEYS = ["name", "createdAt"] as const;`.
 */
export function sortSchema<const K extends readonly [string, ...string[]]>(
  allowedKeys: K,
  defaultDir: SortDir = "asc",
) {
  return z.object({
    sortBy: z.enum(allowedKeys).optional(),
    sortDir: sortDirEnum.default(defaultDir),
  });
}

/** Paginated response wrapper. Use as the output schema shape for paginated actions. */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** Calculates SQL offset from page and pageSize. */
export function getOffset(page: number, pageSize: number): number {
  return (page - 1) * pageSize;
}

/** Builds a PaginatedResult from items, total count, and pagination input. */
export function paginate<T>(
  items: T[],
  total: number,
  pagination: PaginationInput,
): PaginatedResult<T> {
  return {
    items,
    total,
    page: pagination.page,
    pageSize: pagination.pageSize,
    totalPages: Math.ceil(total / pagination.pageSize),
  };
}
